/* Presentation only. The existing theme owns product submissions and analytics. */
(() => {
  const reviewScriptBase = document.currentScript?.src;
  const loadModeratedReviews = () => {
    if (!reviewScriptBase || document.querySelector('script[src*="clearview-reviews.js"]')) return;
    const script = document.createElement('script');
    script.src = new URL('clearview-reviews.js', reviewScriptBase).href;
    script.async = false;
    script.dataset.cvReviewsLoader = 'true';
    document.head.appendChild(script);
  };

  const initialize = (page) => {
    if (page.dataset.cvReady) return;
    page.dataset.cvReady = 'true';
    const slides = [...page.querySelectorAll('[data-cv-slide]')];
    const thumbs = [...page.querySelectorAll('[data-cv-thumb]')];
    const dialog = page.querySelector('[data-cv-dialog]');
    const enlarged = page.querySelector('[data-cv-enlarged]');
    const count = page.querySelector('[data-cv-gallery-count]');
    const zoomIcon = page.querySelector('.cv-gallery__zoom');
    if (zoomIcon) zoomIcon.textContent = '⛶';
    let active = 0;
    let suppressGalleryClickUntil = 0;
    const selectImage = (index) => {
      if (!slides.length) return;
      active = (index + slides.length) % slides.length;
      slides.forEach((slide, i) => { slide.hidden = i !== active; });
      thumbs.forEach((thumb, i) => { thumb.setAttribute('aria-pressed', String(i === active)); });
      const source = slides[active].querySelector('img');
      if (source && enlarged) {
        enlarged.src = source.currentSrc || source.src;
        enlarged.alt = source.alt || `ClearView – Produktbild ${active + 1}`;
      }
      if (count) count.textContent = `${active + 1} / ${slides.length}`;
    };
    const bindSwipe = (target) => {
      if (!target || slides.length < 2) return;
      let start = null;
      target.addEventListener('touchstart', (event) => {
        if (event.touches.length !== 1) { start = null; return; }
        const touch = event.touches[0];
        start = { x: touch.clientX, y: touch.clientY };
      }, { passive: true });
      target.addEventListener('touchend', (event) => {
        if (!start || event.changedTouches.length !== 1) { start = null; return; }
        const touch = event.changedTouches[0];
        const dx = touch.clientX - start.x;
        const dy = touch.clientY - start.y;
        start = null;
        if (Math.abs(dx) < 42 || Math.abs(dx) <= Math.abs(dy) * 1.15) return;
        suppressGalleryClickUntil = Date.now() + 450;
        selectImage(active + (dx < 0 ? 1 : -1));
      }, { passive: true });
      target.addEventListener('touchcancel', () => { start = null; }, { passive: true });
    };
    thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => selectImage(index)));
    slides.forEach((slide, index) => slide.addEventListener('click', () => {
      if (Date.now() < suppressGalleryClickUntil) return;
      selectImage(index);
      if (dialog && typeof dialog.showModal === 'function') dialog.showModal();
    }));
    bindSwipe(page.querySelector('.cv-gallery__stage'));
    bindSwipe(dialog?.querySelector('.cv-dialog__stage'));
    page.querySelector('[data-cv-close]')?.addEventListener('click', () => dialog?.close());
    page.querySelector('[data-cv-prev]')?.addEventListener('click', () => selectImage(active - 1));
    page.querySelector('[data-cv-next]')?.addEventListener('click', () => selectImage(active + 1));
    dialog?.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
    dialog?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        selectImage(active + (event.key === 'ArrowRight' ? 1 : -1));
      }
    });

    const form = page.querySelector('.cv-form');
    const primarySubmit = page.querySelector('[data-cv-submit]');
    const sticky = page.querySelector('[data-cv-sticky]');
    const stickySubmit = page.querySelector('[data-cv-sticky-submit]');
    if (primarySubmit && stickySubmit) {
      const mirrorSubmit = () => { stickySubmit.disabled = primarySubmit.disabled; };
      mirrorSubmit();
      const buttonObserver = new MutationObserver(mirrorSubmit);
      buttonObserver.observe(primarySubmit, { attributes: true, attributeFilter: ['disabled'] });
    }
    if (sticky && form && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(([entry]) => {
        sticky.hidden = entry.isIntersecting && entry.intersectionRatio >= 0.5;
      }, { threshold: [0, 0.5] });
      observer.observe(primarySubmit || form);
    }

    let variants = [];
    try { variants = JSON.parse(page.querySelector('[data-cv-variants]')?.textContent || '[]'); }
    catch (_) { /* The Shopify form remains usable without enhancement. */ }
    const variantControl = page.querySelector('[data-cv-variant]');
    const quantityControl = page.querySelector('[data-qty-input]');
    const updateShipping = () => {
      const variant = variants.find((item) => String(item.id) === variantControl?.value);
      if (!variant) return;
      const quantity = Math.max(1, Number(quantityControl?.value) || 1);
      page.querySelectorAll('[data-cv-shipping]').forEach((note) => {
        note.textContent = variant.price * quantity >= 5500 ? note.dataset.free : note.dataset.paid;
      });
    };
    updateShipping();
    quantityControl?.addEventListener('input', updateShipping);
    quantityControl?.addEventListener('change', updateShipping);
    page.addEventListener('click', (event) => {
      if (event.target.closest('[data-qty-plus], [data-qty-minus]')) queueMicrotask(updateShipping);
    });
    const reviewForm = page.querySelector('.cv-review-form');
    if (reviewForm?.querySelector('[role="alert"], [role="status"]')) {
      reviewForm.closest('details').open = true;
    }
    const currency = page.dataset.currency || 'EUR';
    const money = new Intl.NumberFormat(document.documentElement.lang || 'de-DE', { style: 'currency', currency });
    let delivery = '';
    try { delivery = JSON.parse(page.querySelector('[data-cv-delivery]')?.textContent || '""'); } catch (_) { /* Preserve server-rendered delivery. */ }
    variantControl?.addEventListener('change', () => {
      const variant = variants.find((item) => String(item.id) === variantControl.value);
      if (!variant) return;
      updateShipping();
      page.querySelectorAll('[data-cv-price]').forEach((price) => { price.textContent = money.format(variant.price / 100); });
      if (primarySubmit) primarySubmit.disabled = !variant.available;
      page.querySelectorAll('[data-cv-submit-label]').forEach((label) => { label.textContent = variant.available ? 'In den Warenkorb' : 'Aktuell ausverkauft'; });
      const availability = page.querySelector('[data-cv-availability]');
      if (availability) availability.textContent = variant.available ? `Bestellbar · Lieferung in ${delivery}` : 'Dieses Set ist aktuell nicht verfügbar.';
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
      const variantSource = variant.featured_image?.src?.split('?')[0];
      if (variantSource) {
        const index = slides.findIndex((slide) => slide.querySelector('img')?.src.split('?')[0] === variantSource);
        if (index !== -1) selectImage(index);
      }
    });
    const worldReturn = page.querySelector('.cv-world-return a');
    if (worldReturn) worldReturn.textContent = 'Weitere Produkte aus KABO Clean entdecken';
    page.querySelectorAll('a[href^="#cv-"]').forEach((link) => link.addEventListener('click', (event) => {
      const target = page.querySelector(link.getAttribute('href'));
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    }));
  };
  document.querySelectorAll('[data-cv-page]').forEach(initialize);
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-cv-page]').forEach(initialize);
  });
  loadModeratedReviews();
})();
